/* eslint-disable no-console */
// Core Web Vitals measurement utilities
export interface WebVitalsMetric {
  name: string;
  value: number;
  rating: "good" | "needs-improvement" | "poor";
  delta: number;
  id: string;
}

// Thresholds based on Google's Core Web Vitals recommendations
const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 }, // Largest Contentful Paint
  FID: { good: 100, poor: 300 }, // First Input Delay
  CLS: { good: 0.1, poor: 0.25 }, // Cumulative Layout Shift
  FCP: { good: 1800, poor: 3000 }, // First Contentful Paint
  TTFB: { good: 800, poor: 1800 }, // Time to First Byte
  INP: { good: 200, poor: 500 }, // Interaction to Next Paint
};

export function getMetricRating(
  name: string,
  value: number
): "good" | "needs-improvement" | "poor" {
  const threshold = THRESHOLDS[name as keyof typeof THRESHOLDS];

  if (!threshold) return "good";

  if (value <= threshold.good) return "good";
  if (value <= threshold.poor) return "needs-improvement";

  return "poor";
}

// Enhanced web vitals reporting
export function reportWebVitals(metric: WebVitalsMetric) {
  // In production, send to Google Analytics 4
  if (process.env.NODE_ENV === "production") {
    if (typeof gtag !== "undefined") {
      gtag("event", metric.name, {
        custom_map: { metric_id: "custom_metric" },
        value: Math.round(
          metric.name === "CLS" ? metric.value * 1000 : metric.value
        ),
        metric_id: metric.id,
        metric_value: metric.value,
        metric_delta: metric.delta,
        metric_rating: metric.rating,
      });
    }
  }

  // Development logging with environment variable control
  if (
    process.env.NODE_ENV === "development" &&
    process.env.NEXT_PUBLIC_DEBUG_PERFORMANCE === "true"
  ) {
    console.log(`[Web Vitals] ${metric.name}:`, {
      value: metric.value,
      rating: metric.rating,
      id: metric.id,
      delta: metric.delta,
    });
  }
}

// Performance optimization utilities
export class PerformanceOptimizer {
  private static instance: PerformanceOptimizer;
  private observers: Map<string, PerformanceObserver> = new Map();

  static getInstance(): PerformanceOptimizer {
    if (!PerformanceOptimizer.instance) {
      PerformanceOptimizer.instance = new PerformanceOptimizer();
    }

    return PerformanceOptimizer.instance;
  }

  // Monitor LCP candidates
  monitorLCP(callback?: (entries: PerformanceEntry[]) => void) {
    if (typeof window === "undefined" || !("PerformanceObserver" in window))
      return;

    const observer = new PerformanceObserver(list => {
      const entries = list.getEntries();

      if (callback) callback(entries);

      entries.forEach(entry => {
        if (process.env.NEXT_PUBLIC_DEBUG_PERFORMANCE === "true") {
          console.log("[LCP Candidate]", {
            element: (entry as any).element,
            size: (entry as any).size,
            startTime: entry.startTime,
          });
        }
      });
    });

    observer.observe({ entryTypes: ["largest-contentful-paint"] });
    this.observers.set("lcp", observer);
  }

  // Monitor layout shifts
  monitorCLS(callback?: (entries: PerformanceEntry[]) => void) {
    if (typeof window === "undefined" || !("PerformanceObserver" in window))
      return;

    const observer = new PerformanceObserver(list => {
      const entries = list.getEntries();

      if (callback) callback(entries);

      entries.forEach(entry => {
        if (!(entry as any).hadRecentInput) {
          if (process.env.NEXT_PUBLIC_DEBUG_PERFORMANCE === "true") {
            console.log("[Layout Shift]", {
              value: (entry as any).value,
              sources: (entry as any).sources,
              startTime: entry.startTime,
            });
          }
        }
      });
    });

    observer.observe({ entryTypes: ["layout-shift"] });
    this.observers.set("cls", observer);
  }

  // Monitor resource loading
  monitorResources(callback?: (entries: PerformanceEntry[]) => void) {
    if (typeof window === "undefined" || !("PerformanceObserver" in window))
      return;

    const observer = new PerformanceObserver(list => {
      const entries = list.getEntries();

      if (callback) callback(entries);

      entries.forEach(entry => {
        const resource = entry as PerformanceResourceTiming;

        if (resource.duration > 1000) {
          // Log slow resources
          if (process.env.NEXT_PUBLIC_DEBUG_PERFORMANCE === "true") {
            console.warn("[Slow Resource]", {
              name: resource.name,
              duration: resource.duration,
              size: resource.transferSize,
              type: resource.initiatorType,
            });
          }
        }
      });
    });

    observer.observe({ entryTypes: ["resource"] });
    this.observers.set("resource", observer);
  }

  // Cleanup observers
  disconnect() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
  }

  // Get navigation timing metrics
  getNavigationMetrics(): Record<string, number> | null {
    if (typeof window === "undefined" || !performance.getEntriesByType)
      return null;

    const [navigation] = performance.getEntriesByType(
      "navigation"
    ) as PerformanceNavigationTiming[];

    if (!navigation) return null;

    return {
      dns: navigation.domainLookupEnd - navigation.domainLookupStart,
      tcp: navigation.connectEnd - navigation.connectStart,
      ssl: navigation.connectEnd - navigation.secureConnectionStart,
      ttfb: navigation.responseStart - navigation.requestStart,
      download: navigation.responseEnd - navigation.responseStart,
      domParse: navigation.domContentLoadedEventEnd - navigation.responseEnd,
      domReady: navigation.domContentLoadedEventEnd - navigation.fetchStart,
      pageLoad: navigation.loadEventEnd - navigation.fetchStart,
    };
  }
}

// Image optimization utilities
export const imageOptimization = {
  // Generate responsive image sizes
  generateSizes: (sizes: Array<{ breakpoint?: number; size: string }>) => {
    return sizes
      .map(({ breakpoint, size }) =>
        breakpoint ? `(max-width: ${breakpoint}px) ${size}` : size
      )
      .join(", ");
  },

  // Generate WebP fallback strategy
  getOptimizedSrc: (src: string, format: "webp" | "avif" | "auto" = "auto") => {
    if (src.startsWith("http") || src.startsWith("data:")) return src;

    const base = src.replace(/\.[^.]+$/, "");
    const ext = format === "auto" ? "webp" : format;

    return `${base}.${ext}`;
  },

  // Lazy loading intersection observer
  createLazyLoader: (
    callback: (entries: IntersectionObserverEntry[]) => void
  ) => {
    if (typeof window === "undefined") return null;

    return new IntersectionObserver(callback, {
      rootMargin: "50px 0px",
      threshold: 0.01,
    });
  },
};

// Bundle analysis helpers
export const bundleOptimization = {
  // Analyze bundle sizes (for development)
  analyzeBundleSize: () => {
    if (typeof window === "undefined" || process.env.NODE_ENV !== "development")
      return;

    const scripts = Array.from(document.querySelectorAll("script[src]"));
    const totalSize = scripts.reduce((acc, script) => {
      const src = script.getAttribute("src");

      if (src && src.includes("/_next/")) {
        // Estimate size based on URL (not accurate, but helpful for development)
        return acc + 1;
      }

      return acc;
    }, 0);

    if (process.env.NEXT_PUBLIC_DEBUG_PERFORMANCE === "true") {
      console.log(
        `[Bundle Analysis] Estimated ${totalSize} script chunks loaded`
      );
    }
  },

  // Monitor chunk loading
  monitorChunkLoading: () => {
    if (typeof window === "undefined") return;

    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const [url] = args;
      const startTime = performance.now();

      try {
        const response = await originalFetch(...args);
        const endTime = performance.now();

        if (typeof url === "string" && url.includes("/_next/")) {
          if (process.env.NEXT_PUBLIC_DEBUG_PERFORMANCE === "true") {
            console.log(
              `[Chunk Load] ${url.split("/").pop()}: ${endTime - startTime}ms`
            );
          }
        }

        return response;
      } catch (error) {
        console.error(`[Chunk Load Error] ${url}:`, error);
        throw error;
      }
    };
  },
};

declare global {
  function gtag(...args: any[]): void;
}
